import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { TransitionPromptHook } from 'history'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import isFunction from 'lodash/isFunction'
import useCallbackRef from '@signal/core-hooks/useCallbackRef'
import { isRefInsideHiddenTransition } from '@signal/utils/transitionGroupHelpers'
import {
  ROUTE_CHANGE_RESPONSE_ALLOW,
  ROUTE_CHANGE_RESPONSE_UNSAVED_PROMPT,
  CHOICE_ID_YES
} from './constants'
import {
  ModalPromptContextType,
  OnAllowType,
  RouteChangeHocExtraPropsType,
  RegisteredRouteChangeRecordType,
  ShouldBlockNavigateCallbackType,
  ShouldBlockNavigateResponsesType
} from './types'
import { ModalPromptContext } from './Context'

/**
 * this hoc sets up a route change hook that can issue modal confirmation prompts on route change while your component
 * is mounted.
 * @param {React.Component} WrappedComponent - a component to wrap with a route change prompt.
 *   The component will also be wrapped with the withRouter() HOC.  your component must implement
 *   a registerRouteChangePrompt handler, which it should call to register a handler which will be called when
 *   a navigation occurs.  registerRouteChangePrompt accepts RegisteredRouteChangeRecordType, a callback function and
 *   a useRef() reference pointing to an element in your markup
 *     (to determine if you are on screen, or hidden by react-transition-group)
 *   the callback function in the record can be a callback function to call when a route change happens, or undefined
 *   if no blocking is needed.  The function you pass to registerRouteChangePrompt will receive the location
 *   being navigated to and must return a promise which returns one of the ShouldBlockNavigateResponsesType
 *     allow - allow the navigation
 *     block - block the navigation
 *     unsaved-prompt - show a standard unsaved navigation confirmation prompt
 *   you can issue your own modals from this callback, or you can return 'unsaved-prompt' to get a standard unsaved
 *   content modal issued by this HOC
 * @return {React.Component} - your component, wrapped with withRouter() and a registerRouteChangePrompt handler
 */
export function withRouteChangePrompt<T>(
  WrappedComponent: React.ComponentType<
    T & RouteChangeHocExtraPropsType & RouteComponentProps
  >
): React.ComponentType<T & RouteChangeHocExtraPropsType & RouteComponentProps> {
  type ComponentPropsType = T &
    RouteChangeHocExtraPropsType &
    RouteComponentProps
  const WrapperComponent = withRouter(
    (props: ComponentPropsType): JSX.Element => {
      const { history } = props
      const { promptWithYesNo } = useContext(ModalPromptContext)
      const childModalContextRef = useRef<ModalPromptContextType>()
      const registeredRouteChangePromptsRef = useRef<
        Array<RegisteredRouteChangeRecordType>
      >([])
      const registeredRouteChangePromptsNextRef = useRef<
        Array<RegisteredRouteChangeRecordType>
      >([])
      const invokingRegisteredRouteChangePromptsRef = useRef<boolean>(false)

      const routeCallbackRef = useRef<ShouldBlockNavigateCallbackType>()
      const handleRouteChangeRequest = useCallbackRef(
        routeCallbackRef,
        (location: Location, onAllow: OnAllowType): boolean => {
          //
          // we have seen a route change - are there any registered callback handlers?
          //
          const routeChangePrompts = registeredRouteChangePromptsRef.current
          if (routeChangePrompts.length > 0) {
            invokingRegisteredRouteChangePromptsRef.current = true

            // recursive function to handle the next callback record(s)
            const handleNextCallbackRecord = (callbackIndex: number): void => {
              const callbackRecord = routeChangePrompts[callbackIndex]
              const shouldBlockNavigateCallback =
                callbackRecord.shouldBlockNavigate
              const callbackRef = callbackRecord.ref

              // call this after prompt if the current registered handler does not want to block the navigation
              const handleNextOrUnblock = (): void => {
                if (routeChangePrompts.length > callbackIndex + 1) {
                  // more handlers - call next one
                  handleNextCallbackRecord(callbackIndex + 1)
                } else {
                  // no more handlers - allow navigation.

                  // clear all callback records, and all pending callback records, since we are going to a new page
                  while (routeChangePrompts.length) {
                    routeChangePrompts.pop()
                  }
                  const routeChangePromptsNext =
                    registeredRouteChangePromptsNextRef.current
                  while (routeChangePromptsNext.length) {
                    routeChangePromptsNext.pop()
                  }

                  invokingRegisteredRouteChangePromptsRef.current = false

                  if (isFunction(onAllow)) {
                    // allow the navigation
                    onAllow()
                  } else {
                    // if no allow handler, we blocked a navigation, so re-push it
                    history.push(location.pathname)
                  }
                }
              }

              // call this after prompt if the current registered handler does want to block the navigation
              const keepBlocking = (): void => {
                const routeChangePromptsNext =
                  registeredRouteChangePromptsNextRef.current
                // if any new registrations came in while we were showing modals, then move those over to the
                // true registration set now and handle them on next navigate.  This is important to ensure
                // we don't "forget" callbacks after route change modals are displayed but the user chooses to
                // stay on the page.
                while (routeChangePromptsNext.length) {
                  routeChangePrompts.push(
                    routeChangePromptsNext.pop() as RegisteredRouteChangeRecordType
                  )
                }

                invokingRegisteredRouteChangePromptsRef.current = false
              }

              if (
                shouldBlockNavigateCallback &&
                !isRefInsideHiddenTransition(callbackRef)
              ) {
                shouldBlockNavigateCallback(location).then(
                  (blockResponse: ShouldBlockNavigateResponsesType): void => {
                    const promptWithYesNoUse = childModalContextRef.current
                      ? childModalContextRef.current.promptWithYesNo
                      : promptWithYesNo
                    switch (blockResponse) {
                      case ROUTE_CHANGE_RESPONSE_ALLOW:
                        handleNextOrUnblock()
                        break
                      case ROUTE_CHANGE_RESPONSE_UNSAVED_PROMPT:
                        promptWithYesNoUse({
                          title: 'Are you sure?',
                          descriptionText:
                            'You have unsaved changes.  Are you sure you want to leave this page?'
                        }).then((choice: string): void => {
                          if (choice === CHOICE_ID_YES) {
                            handleNextOrUnblock()
                          } else {
                            keepBlocking()
                          }
                        })
                        break
                      default:
                        keepBlocking()
                        break
                    }
                  }
                )
              } else {
                handleNextOrUnblock()
              }
            }

            // start recursing through the route change callbacks
            handleNextCallbackRecord(0)

            // always tell react-router to block if we have pending callbacks to resolve
            // if we should decide to continue with this navigation after all callbacks, then this nagigation will be
            // re-pushed by code above
            return false
          }

          // no handlers, go ahead and allow if we have an allow callback
          if (isFunction(onAllow)) {
            onAllow()
          }

          return true
        },
        [history, promptWithYesNo]
      )

      // navigations that happen outside of react-router will trigger a beforeunload event
      // you can cancel those with a prompt, but only a default browser-styled prompt, because otherwise the
      // browser could be hijacked by someone who prevents the unload.
      const handleBeforeUnload = useCallback(() => {
        const routeChangePrompts = registeredRouteChangePromptsRef.current
        const validPrompt = routeChangePrompts.find(callbackRecord => {
          const shouldBlockNavigateCallback = callbackRecord.shouldBlockNavigate
          const callbackRef = callbackRecord.ref
          return (
            shouldBlockNavigateCallback &&
            !isRefInsideHiddenTransition(callbackRef)
          )
        })
        if (validPrompt) {
          return 'You have unsaved changes.  Are you sure you want to leave this page?'
        }
      }, [])

      useEffect(() => {
        const unregisterBlock = history.block(
          handleRouteChangeRequest as TransitionPromptHook<unknown>
        )

        Object.defineProperty(window, 'onbeforeunload', {
          configurable: true,
          writable: true
        })

        window.onbeforeunload = handleBeforeUnload

        return (): void => {
          unregisterBlock()
          window.onbeforeunload = null
        }
      }, [handleBeforeUnload, handleRouteChangeRequest, history])

      const handleRegisterRouteChangePrompt = useCallback(
        (record: RegisteredRouteChangeRecordType): void => {
          // find an existing callback registration record for this caller
          // generating a modal can cause react-transition-group to start doing things(!).  Because of this,
          // if we receive new callbacks from newly mounting components while we are in the middle of our recursive
          // modal callbacks on route change, save those hooks and wait until AFTER the current set of modals are
          // dimsissed before we process them
          const routeChangePrompts =
            invokingRegisteredRouteChangePromptsRef.current
              ? registeredRouteChangePromptsNextRef.current
              : registeredRouteChangePromptsRef.current
          const existingRecord = record.ref.current
            ? routeChangePrompts.find(
                ({ ref }) =>
                  ref.current &&
                  ref.current.isSameNode(record.ref.current as Node)
              )
            : undefined
          if (existingRecord) {
            existingRecord.shouldBlockNavigate = record.shouldBlockNavigate
          } else {
            routeChangePrompts.push(record)
          }
        },
        []
      )

      const handleModalContext = useCallback(
        (modalContext: ModalPromptContextType) => {
          childModalContextRef.current = modalContext
        },
        []
      )

      return (
        <WrappedComponent
          {...props}
          onModalContext={handleModalContext}
          registerRouteChangePrompt={handleRegisterRouteChangePrompt}
          invokeRouteChangeRequest={handleRouteChangeRequest}
        />
      )
    }
  )

  return WrapperComponent as unknown as React.ComponentType<ComponentPropsType>
}

export default withRouteChangePrompt
